<template>
  <div>
    <custom-collapse id="questions-collapse" class="px-4">
      <draggable :list="model" :disabled="!dragging" @end="onEndDrag">
        <custom-collapse-item
          v-for="(item, key) in model"
          :id="`question-collapse-item-${key + 1}`"
          :key="`question-collapse-item-${key + 1}`"
          :ref="`question-collapse-item-${key + 1}`"
          :auto-expand-collapse-enabled="false"
          :button-icon="model.length > 1 ? 'far fa-times-circle color-red' : ''"
          :class="{ invalid: item.valid === false }"
          :disabled="isReadOnly || dragging"
          :expanded="item.expanded"
          :keep-btn-visible="false"
          :title="item.name | title(key)"
          class="my-3 rounded collapse-item question"
          parent-id="questions-collapse"
          expanded-icon="fas fa-plus-circle text-dark-blue"
          :show-icon-when-button-disabled="dragging"
          icon-class-when-button-disabled="fas fa-grip-vertical d-flex flex-fill justify-content-end align-items-center data-label"
          read-only-icon="fas fa-plus-circle color-gray-2"
          @actionButtonClick="quit(key)"
          @expandButtonClick="expand(item, key)"
        >
          <validation-observer
            novalidate
            tag="form"
            :ref="`question-${key}`"
            @submit.prevent="add"
          >
            <div class="mb-3">
              <validation-provider
                mode="lazy"
                rules="required"
                v-slot="{ errors }"
              >
                <custom-input
                  v-model="item.name"
                  label="Pregunta o Descripción:"
                  placeholder="Escribe tu Pregunta o Descripción"
                  custom-error-msg="El campo es requerido."
                  :error="errors[0]"
                  :disabled="isReadOnly"
                />
              </validation-provider>
            </div>
            <div class="row mt-3">
              <div class="col-12 col-md-6">
                <validation-provider v-slot="{ errors }" rules="required">
                  <label class="data-label">
                    Opción de Respuesta:
                  </label>
                  <b-form-select
                    v-model="item.typeId"
                    :options="options"
                    :class="{ invalid: errors.length > 0 }"
                    class="custom-bg-green fa font-weight-normal"
                    :disabled="isReadOnly"
                  >
                    <template #first>
                      <b-form-select-option :value="null" selected hidden>
                        -- Seleccione --
                      </b-form-select-option>
                    </template>
                  </b-form-select>

                  <custom-error
                    v-if="errors.length > 0"
                    message="El campo es requerido."
                    variant="no-border mt-1"
                  />
                </validation-provider>
              </div>
            </div>
            <div class="mt-3">
              <div v-if="isOpenText(item)">
                <open-text />
              </div>
              <div v-else-if="isSimpleChoiceHorizontal(item)">
                <simple-choice-horizontal
                  :is-read-only="isReadOnly"
                  v-model="item.answers"
                />
              </div>
              <div v-else-if="isSimpleChoiceHorizontalWithDependency(item)">
                <simple-choice-horizontal-with-dependency
                  v-model="item.answers"
                  :types="dependencyOptions"
                  :child-questions.sync="item.childQuestions"
                  :is-read-only="isReadOnly"
                />
              </div>
              <div v-else-if="item.type" class="row">
                <div class="col-12 col-md-6">
                  <custom-result-not-found
                    text="No hay un control creado para esta opción de respuesta."
                  />
                </div>
              </div>
            </div>
          </validation-observer>
        </custom-collapse-item>
      </draggable>
    </custom-collapse>
    <div class="d-flex flex-row-reverse">
      <action-button
        :disabled="dragging || isReadOnly"
        i="fas fa-file-plus"
        text="Añadir Pregunta"
        variant="success"
        @click="add"
      />
    </div>
  </div>
</template>

<script>
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';

import { ValidationObserver, ValidationProvider } from 'vee-validate';
import CustomInput from 'CustomInput';
import CustomTextArea from 'CustomTextArea';
import ActionButton from 'ActionButton';
import CustomError from 'custom-error';
import CustomResultNotFound from 'custom-result-not-found';
import Draggable from 'vuedraggable';

import CustomCollapse from '@/components/common/CustomCollapse.vue';
import CustomCollapseItem from '@/components/common/CustomCollapseItem.vue';

import Types from '@/utils/constants/operations-dashboard-management/questionnaireQuestionTypes';

import {
  OpenText,
  SimpleChoiceHorizontal,
  SimpleChoiceHorizontalWithDependency,
} from './controls';

export default {
  name: 'QuestionnaireQuestions',
  components: {
    ValidationObserver,
    ValidationProvider,
    CustomInput,
    CustomTextArea,
    ActionButton,
    CustomError,
    CustomResultNotFound,
    Draggable,
    CustomCollapse,
    CustomCollapseItem,
    OpenText,
    SimpleChoiceHorizontal,
    SimpleChoiceHorizontalWithDependency,
  },
  mixins: [alertMixin],
  props: {
    value: {
      type: Array,
      required: true,
    },
    types: {
      type: Array,
      required: true,
    },
    dragging: {
      type: Boolean,
      default: () => false,
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  filters: {
    title(value, key) {
      return value ?? `Pregunta ${key + 1}`;
    },
  },
  async mounted() {
    if (this.model.length === 0) await this.add();
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    options() {
      return this.types.map((x) => ({
        value: x.id,
        html: `${x.icon} ${x.name}`,
      }));
    },
    dependencyOptions() {
      return this.options.filter(
        ({ value }) => value !== Types.SimpleChoiceHorizontalWithDependency
      );
    },
    isOpenText() {
      return ({ typeId }) => typeId === Types.Text;
    },
    isSimpleChoiceHorizontal() {
      return ({ typeId }) => typeId === Types.SimpleChoiceHorizontal;
    },
    isSimpleChoiceHorizontalWithDependency() {
      return ({ typeId }) =>
        typeId === Types.SimpleChoiceHorizontalWithDependency;
    },
  },
  methods: {
    expand(item, key) {
      this.model
        .filter((_, i) => i !== key)
        .forEach((x) => (x.expanded = false));
      setTimeout(() => (item.expanded = !item.expanded), 100);
    },
    async add() {
      const valid = await this.validate();
      if (!valid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }
      const item = {
        name: null,
        type: null,
        answers: [],
        childQuestions: [],
        valid: true,
        expanded: false,
      };
      this.model.push(item);
      this.expand(item, this.model.length - 1);
      this.$emit('add', item);
    },
    async quit(item, index) {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea remover la pregunta documentada? `,
        'Si',
        'No'
      );

      if (!confirmed) return;
      this.model.splice(index, 1);
      this.$emit('quit', item, index);
    },
    async validate() {
      if (!this.model.length) return true;
      const forms = this.model.map((item, key) => {
        const validator = this.$refs[`question-${key}`][0];
        return validator.validate().then((val) => (item.valid = val));
      });
      await Promise.all(forms);
      return this.model.every((x) => x.valid);
    },
    onEndDrag() {
      this.model.forEach((_, key) => this.$refs[`question-${key}`][0].reset());
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/v2/brand_variables.scss';
.collapse-item ::v-deep .text-dark-blue {
  color: #405c64;
}
.form-control,
::v-deep .form-control,
.custom-bg-green {
  background-color: #f1f6f4;
  font-family: 'Font Awesome 5 Pro', 'Lato', sans-serif;
}
.collapse-item ::v-deep .shadow2 {
  background-color: white !important;
}

.collapse-item ::v-deep > .card-header {
  background-color: white !important;
}
.custom-select.invalid,
.collapse-item.question.invalid {
  border: 1px solid #ff0018 !important;
}

.card.expectative .card-body {
  background-color: rgba(0, 0, 0, 0.03) !important;
}
</style>
